'use client';

import React, { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Experience {
  id: number;
  title: string;
  host: string;
  hostAvatar: string;
  hostBio: string;
  hostJoined: string;
  hostReviews: number;
  location: string;
  country: string;
  category: string;
  duration: string;
  groupSize: string;
  price: number;
  currency: string;
  rating: number;
  reviews: number;
  images: { src: string; alt: string }[];
  tag?: string;
  tagColor?: string;
  languages: string[];
  highlights: string[];
  description: string;
  included: string[];
  notIncluded: string[];
  itinerary: { time: string; activity: string }[];
  isSuperhost?: boolean;
  reviewsList: { name: string; avatar: string; date: string; rating: number; comment: string }[];
}

const experiencesData: Experience[] = [
  {
    id: 1,
    title: 'Sunrise Gorilla Trekking in Bwindi',
    host: 'Samuel Kato',
    hostAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80',
    hostBio: 'Samuel has been guiding gorilla treks for over 12 years. A certified wildlife ranger and passionate conservationist, he brings deep knowledge of Bwindi\'s ecosystem and its mountain gorilla families.',
    hostJoined: 'March 2018',
    hostReviews: 214,
    location: 'Bwindi',
    country: 'Uganda',
    category: 'wildlife',
    duration: '6 hours',
    groupSize: 'Up to 8',
    price: 120,
    currency: 'USD',
    rating: 4.98,
    reviews: 214,
    images: [
      { src: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200&q=80', alt: 'Mountain gorilla family in the misty Bwindi Impenetrable Forest at sunrise' },
      { src: 'https://images.unsplash.com/photo-1564349683136-77e08dba1ef7?w=800&q=80', alt: 'Close-up of a silverback gorilla resting in dense forest vegetation' },
      { src: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&q=80', alt: 'Dense tropical forest canopy in Bwindi Impenetrable National Park Uganda' },
      { src: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=800&q=80', alt: 'Trekkers walking through misty mountain forest on a guided gorilla trek' },
    ],
    tag: 'Bestseller',
    tagColor: 'bg-[#4088C6]',
    languages: ['English', 'Luganda'],
    highlights: ['Expert ranger guide', 'Gorilla permit included', 'Forest breakfast', 'Small group (max 8)', 'Conservation briefing'],
    description: 'Wake before dawn and venture deep into the ancient Bwindi Impenetrable Forest — one of Africa\'s most biodiverse ecosystems — to spend a precious hour with a wild mountain gorilla family. Your certified ranger guide Samuel will lead you through misty trails, sharing knowledge of the forest\'s flora, fauna, and the ongoing conservation efforts that protect these magnificent primates. A forest breakfast awaits you at a scenic clearing before the trek begins. This is a once-in-a-lifetime encounter that leaves every guest speechless.',
    included: ['Gorilla trekking permit', 'Certified ranger guide', 'Forest breakfast', 'Bottled water', 'Walking sticks', 'Porter service (optional)'],
    notIncluded: ['Park entrance fee', 'Transport to Bwindi', 'Gratuities', 'Travel insurance'],
    itinerary: [
      { time: '5:30 AM', activity: 'Meet at Bwindi park gate, briefing by ranger' },
      { time: '6:00 AM', activity: 'Forest breakfast at scenic clearing' },
      { time: '6:45 AM', activity: 'Trek begins — follow gorilla family trails' },
      { time: '9:00 AM', activity: 'Gorilla encounter — 1 hour with the family' },
      { time: '10:30 AM', activity: 'Return trek through forest' },
      { time: '12:00 PM', activity: 'Debrief and certificate presentation' },
    ],
    isSuperhost: true,
    reviewsList: [
      { name: 'Emma W.', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80', date: 'July 2025', rating: 5, comment: 'Absolutely life-changing. Samuel\'s knowledge of the gorillas and the forest is extraordinary. The sunrise trek was magical — we were the only group out there.' },
      { name: 'James O.', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&q=80', date: 'June 2025', rating: 5, comment: 'Worth every penny. The gorilla family was incredible and Samuel made sure everyone had a perfect view. The forest breakfast was a lovely touch.' },
      { name: 'Priya S.', avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&q=80', date: 'May 2025', rating: 5, comment: 'I\'ve done many wildlife experiences across Africa — this was the best. Samuel is a true professional and the small group size made it feel very personal.' },
    ],
  },
  {
    id: 2,
    title: 'White Water Rafting on the Nile',
    host: 'Amara Osei',
    hostAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&q=80',
    hostBio: 'Amara is a certified white water rafting instructor with 8 years of experience on the Nile. He has guided over 2,000 guests safely through Grade 5 rapids and is passionate about adventure tourism in Uganda.',
    hostJoined: 'January 2019',
    hostReviews: 389,
    location: 'Jinja',
    country: 'Uganda',
    category: 'adventure',
    duration: '4 hours',
    groupSize: 'Up to 12',
    price: 75,
    currency: 'USD',
    rating: 4.95,
    reviews: 389,
    images: [
      { src: 'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=1200&q=80', alt: 'Rafters navigating powerful white water rapids on the Nile River in Jinja Uganda' },
      { src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80', alt: 'Aerial view of the Nile River with lush green banks and white water sections' },
      { src: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&q=80', alt: 'Raft crew celebrating after successfully navigating a major Nile rapid' },
      { src: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=800&q=80', alt: 'Safety kayaker escorting rafters through calm section of the Nile' },
    ],
    tag: 'Top rated',
    tagColor: 'bg-blue-500',
    languages: ['English'],
    highlights: ['Grade 5 rapids', 'Safety briefing', 'Lunch included', 'All equipment provided', 'Professional photos'],
    description: 'Tackle the legendary rapids of the Nile at Jinja — the adventure capital of East Africa. Amara and his team will guide you through some of the world\'s most thrilling white water, including Grade 5 rapids that will get your adrenaline pumping. No experience is necessary — the comprehensive safety briefing and expert guides ensure everyone has an incredible and safe experience. A riverside lunch is included after the adventure.',
    included: ['All rafting equipment', 'Safety helmet and life jacket', 'Professional guide', 'Riverside lunch', 'Safety kayakers', 'First aid support'],
    notIncluded: ['Transport to Jinja', 'Professional photos (available for purchase)', 'Gratuities'],
    itinerary: [
      { time: '8:00 AM', activity: 'Arrival and registration at Nile base camp' },
      { time: '8:30 AM', activity: 'Safety briefing and equipment fitting' },
      { time: '9:00 AM', activity: 'First rapids — Grade 2-3 warm-up section' },
      { time: '10:00 AM', activity: 'Main rapids — Grade 4-5 challenge section' },
      { time: '11:30 AM', activity: 'Calm water section and swimming break' },
      { time: '12:30 PM', activity: 'Riverside lunch and debrief' },
    ],
    isSuperhost: true,
    reviewsList: [
      { name: 'Tom B.', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80', date: 'August 2025', rating: 5, comment: 'Best day of my entire trip! Amara is an incredible guide — funny, professional, and made us feel completely safe even on the biggest rapids.' },
      { name: 'Lucia M.', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80', date: 'July 2025', rating: 5, comment: 'I was terrified before we started but Amara\'s briefing gave me so much confidence. The Grade 5 rapids were insane — in the best possible way!' },
      { name: 'Alex K.', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&q=80', date: 'June 2025', rating: 4, comment: 'Fantastic experience. The Nile is absolutely stunning and the rapids are world-class. Highly recommend for any adventure lover.' },
    ],
  },
  {
    id: 3,
    title: 'Traditional Ugandan Cooking Class',
    host: 'Grace Namukasa',
    hostAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80',
    hostBio: 'Grace is a professional chef and food educator who has been sharing Uganda\'s rich culinary heritage for over a decade. She sources all ingredients from local markets and farms, supporting the community she grew up in.',
    hostJoined: 'June 2021',
    hostReviews: 156,
    location: 'Kampala',
    country: 'Uganda',
    category: 'food',
    duration: '3 hours',
    groupSize: 'Up to 6',
    price: 45,
    currency: 'USD',
    rating: 4.92,
    reviews: 156,
    images: [
      { src: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80', alt: 'Hands-on traditional Ugandan cooking class with local ingredients and spices' },
      { src: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=800&q=80', alt: 'Colourful array of fresh Ugandan vegetables and spices laid out for cooking class' },
      { src: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80', alt: 'Students learning to prepare traditional Ugandan matoke banana dish' },
      { src: 'https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800&q=80', alt: 'Finished traditional Ugandan meal spread ready to be enjoyed after cooking class' },
    ],
    tag: 'New',
    tagColor: 'bg-green-500',
    languages: ['English', 'Luganda'],
    highlights: ['Market visit', 'Cook 5 dishes', 'Recipe booklet', 'Eat what you cook', 'Small group'],
    description: 'Discover the soul of Uganda through its food. Grace will take you on a morning market visit to source fresh, seasonal ingredients before returning to her home kitchen to cook five traditional dishes together. You\'ll learn to prepare matoke, groundnut stew, rolex (Uganda\'s famous street food), and more — all from scratch. The class ends with a shared meal and you\'ll take home a recipe booklet to recreate the dishes at home.',
    included: ['Market visit', 'All ingredients', 'Cooking instruction', 'Shared meal', 'Recipe booklet', 'Apron to keep'],
    notIncluded: ['Transport to Kampala', 'Alcoholic beverages', 'Gratuities'],
    itinerary: [
      { time: '9:00 AM', activity: 'Meet Grace at Nakasero Market' },
      { time: '9:30 AM', activity: 'Market tour — selecting fresh ingredients' },
      { time: '10:00 AM', activity: 'Arrive at Grace\'s kitchen, prep begins' },
      { time: '10:30 AM', activity: 'Cook matoke, groundnut stew, and chapati' },
      { time: '11:15 AM', activity: 'Prepare rolex and fresh juice' },
      { time: '12:00 PM', activity: 'Shared meal and recipe booklet handover' },
    ],
    isSuperhost: false,
    reviewsList: [
      { name: 'Sarah L.', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80', date: 'August 2025', rating: 5, comment: 'Grace is an amazing host and teacher. The market visit was so fun and the food we cooked was absolutely delicious. I\'ve already made the groundnut stew at home!' },
      { name: 'Marco T.', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&q=80', date: 'July 2025', rating: 5, comment: 'Such a warm and authentic experience. Grace\'s passion for Ugandan food is infectious. The rolex was a revelation — I had no idea street food could be this good.' },
      { name: 'Yuki H.', avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&q=80', date: 'June 2025', rating: 4, comment: 'Wonderful morning. Grace is incredibly knowledgeable and the small group size meant we all got plenty of hands-on time. Highly recommend.' },
    ],
  },
];

// Fill remaining experiences with minimal data
for (let i = 4; i <= 8; i++) {
  experiencesData.push({
    id: i,
    title: ['Sunset Kayaking on Lake Victoria', 'Kampala Street Art & Culture Walk', 'Chimpanzee Tracking in Kibale Forest', 'Boda Boda City Tour with Local Guide', 'Murchison Falls Boat Safari'][i - 4],
    host: ['David Mugisha', 'Fatima Nakato', 'Robert Byaruhanga', 'Moses Ssekandi', 'Christine Akello'][i - 4],
    hostAvatar: ['https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80', 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80', 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&q=80', 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&q=80', 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=80&q=80'][i - 4],
    hostBio: 'A passionate local expert with years of experience sharing the beauty of Uganda with visitors from around the world.',
    hostJoined: 'January 2020',
    hostReviews: [98, 203, 142, 87, 178][i - 4],
    location: ['Entebbe', 'Kampala', 'Kibale', 'Kampala', 'Murchison Falls'][i - 4],
    country: 'Uganda',
    category: ['water', 'culture', 'wildlife', 'culture', 'wildlife'][i - 4],
    duration: ['2.5 hours', '3 hours', '5 hours', '4 hours', '3 hours'][i - 4],
    groupSize: ['Up to 10', 'Up to 15', 'Up to 6', 'Up to 4', 'Up to 20'][i - 4],
    price: [55, 30, 95, 35, 65][i - 4],
    currency: 'USD',
    rating: [4.89, 4.87, 4.96, 4.83, 4.94][i - 4],
    reviews: [98, 203, 142, 87, 178][i - 4],
    images: [
      { src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80', alt: 'Scenic experience in Uganda with local guide' },
      { src: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80', alt: 'Beautiful Ugandan landscape during guided experience' },
    ],
    languages: ['English'],
    highlights: ['Expert local guide', 'Small group', 'Memorable experience'],
    description: 'An unforgettable experience hosted by a passionate local expert. Discover the beauty and culture of Uganda in an intimate, small-group setting.',
    included: ['Expert guide', 'All equipment', 'Refreshments'],
    notIncluded: ['Transport', 'Gratuities'],
    itinerary: [
      { time: 'Start', activity: 'Meet your host and briefing' },
      { time: 'Mid', activity: 'Main experience activity' },
      { time: 'End', activity: 'Wrap-up and farewell' },
    ],
    isSuperhost: [false, true, true, false, true][i - 4],
    reviewsList: [
      { name: 'Guest', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80', date: 'July 2025', rating: 5, comment: 'An incredible experience — highly recommend to anyone visiting Uganda!' },
    ],
  });
}

export default function ExperienceDetailPage() {
  const params = useParams();
  const id = Number(params?.id);
  const experience = experiencesData.find((e) => e.id === id) ?? experiencesData[0];

  const [activeImage, setActiveImage] = useState(0);
  const [guests, setGuests] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [booked, setBooked] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const total = experience.price * guests;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link href="/home" className="hover:text-gray-800 transition-colors">Home</Link>
            <span>/</span>
            <Link href="/experiences" className="hover:text-gray-800 transition-colors">Experiences</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium truncate">{experience.title}</span>
          </div>
        </div>

        <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* Title */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                {experience.tag && (
                  <span className={`inline-block ${experience.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2`}>
                    {experience.tag}
                  </span>
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{experience.title}</h1>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-900">{experience.rating}</span>
                    <span className="text-sm text-gray-500">({experience.reviews} reviews)</span>
                  </div>
                  <span className="text-gray-300">·</span>
                  <span className="text-sm text-gray-600">{experience.location}, {experience.country}</span>
                  {experience.isSuperhost && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-sm font-semibold text-gray-800">Superhost</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Image gallery */}
          <div className="grid grid-cols-4 gap-2 rounded-2xl overflow-hidden mb-8 h-[340px] md:h-[420px]">
            <div className="col-span-4 md:col-span-2 relative cursor-pointer" onClick={() => setActiveImage(0)}>
              <AppImage
                src={experience.images[0]?.src}
                alt={experience.images[0]?.alt}
                fill
                className="object-cover hover:brightness-95 transition-all"
              />
            </div>
            <div className="hidden md:grid col-span-2 grid-rows-2 gap-2">
              {experience.images.slice(1, 4).map((img, i) => (
                <div key={i} className={`relative cursor-pointer ${i === 1 ? 'row-span-1' : ''}`} onClick={() => setActiveImage(i + 1)}>
                  <AppImage
                    src={img.src}
                    alt={img.alt}
                    fill
                    className="object-cover hover:brightness-95 transition-all"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Content + Booking */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 pb-16">
            {/* Left: Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Quick stats */}
              <div className="flex flex-wrap gap-4 pb-6 border-b border-gray-100">
                {[
                  { icon: '⏱️', label: 'Duration', value: experience.duration },
                  { icon: '👥', label: 'Group size', value: experience.groupSize },
                  { icon: '🌍', label: 'Languages', value: experience.languages.join(', ') },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 flex-1 min-w-[140px]">
                    <span className="text-xl">{stat.icon}</span>
                    <div>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-sm font-semibold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">About this experience</h2>
                <p className="text-gray-600 leading-relaxed">{experience.description}</p>
              </div>

              {/* Highlights */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">What you'll do</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {experience.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#4088C6]/10 rounded-xl px-4 py-3">
                      <div className="w-6 h-6 bg-[#4088C6] rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-800">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Itinerary */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Itinerary</h2>
                <div className="relative pl-6 border-l-2 border-[#4088C6]/20 space-y-4">
                  {experience.itinerary.map((step, i) => (
                    <div key={i} className="relative">
                      <div className="absolute -left-[25px] w-4 h-4 bg-[#4088C6] rounded-full border-2 border-white shadow-sm" />
                      <div className="bg-gray-50 rounded-xl px-4 py-3">
                        <span className="text-xs font-bold text-[#4088C6] uppercase tracking-wide">{step.time}</span>
                        <p className="text-sm text-gray-800 mt-0.5">{step.activity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Included / Not included */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-3">What's included</h3>
                  <ul className="space-y-2">
                    {experience.included.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-3">Not included</h3>
                  <ul className="space-y-2">
                    {experience.notIncluded.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Host */}
              <div className="border-t border-gray-100 pt-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Meet your host</h2>
                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <AppImage src={experience.hostAvatar} alt={`${experience.host} host profile photo`} width={64} height={64} className="object-cover" />
                    </div>
                    {experience.isSuperhost && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#4088C6] rounded-full flex items-center justify-center border-2 border-white">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{experience.host}</p>
                    {experience.isSuperhost && <p className="text-xs text-[#4088C6] font-semibold">Superhost</p>}
                    <p className="text-xs text-gray-500 mt-0.5">Hosting since {experience.hostJoined} · {experience.hostReviews} reviews</p>
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{experience.hostBio}</p>
                  </div>
                </div>
              </div>

              {/* Reviews */}
              <div className="border-t border-gray-100 pt-8">
                <div className="flex items-center gap-3 mb-6">
                  <svg className="w-5 h-5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <h2 className="text-xl font-bold text-gray-900">{experience.rating} · {experience.reviews} reviews</h2>
                </div>
                <div className="space-y-6">
                  {experience.reviewsList.map((review, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <AppImage src={review.avatar} alt={`${review.name} reviewer profile photo`} width={40} height={40} className="object-cover" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{review.name}</p>
                          <span className="text-gray-300">·</span>
                          <p className="text-xs text-gray-500">{review.date}</p>
                        </div>
                        <div className="flex items-center gap-0.5 mt-0.5 mb-1.5">
                          {Array.from({ length: review.rating }).map((_, j) => (
                            <svg key={j} className="w-3 h-3 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Booking panel */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-2xl font-bold text-gray-900">${experience.price}</span>
                  <span className="text-gray-500 text-sm">/ person</span>
                </div>

                {booked ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="font-bold text-gray-900 text-lg">Booking confirmed!</p>
                    <p className="text-sm text-gray-500 mt-1">You'll receive a confirmation email shortly.</p>
                    <button
                      onClick={() => setBooked(false)}
                      className="mt-4 text-sm text-[#4088C6] font-semibold hover:underline"
                    >
                      Book another date
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Date</label>
                        <input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#4088C6]/40 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Guests</label>
                        <div className="flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-2.5">
                          <button
                            onClick={() => setGuests(Math.max(1, guests - 1))}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-600"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="flex-1 text-center text-sm font-semibold text-gray-900">{guests} guest{guests !== 1 ? 's' : ''}</span>
                          <button
                            onClick={() => setGuests(Math.min(parseInt(experience.groupSize.replace(/\D/g, '')) || 20, guests + 1))}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-600"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4 mb-4 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>${experience.price} × {guests} guest{guests !== 1 ? 's' : ''}</span>
                        <span>${total}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-100">
                        <span>Total</span>
                        <span>${total}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => setBooked(true)}
                      className="w-full bg-[#4088C6] hover:bg-[#3070a6] text-white font-bold py-3.5 rounded-xl transition-colors text-sm shadow-md hover:shadow-lg"
                    >
                      Reserve
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-2">You won't be charged yet</p>
                  </>
                )}

                <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage src={experience.hostAvatar} alt={`${experience.host} host thumbnail`} width={40} height={40} className="object-cover" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Hosted by</p>
                    <p className="text-sm font-semibold text-gray-900">{experience.host}</p>
                  </div>
                  {experience.isSuperhost && (
                    <span className="ml-auto text-xs font-bold text-[#4088C6] bg-[#4088C6]/10 px-2 py-1 rounded-full">Superhost</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
